import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../popups/Modal";
import useApiFromGetPost from "../utils/useApiGetPost";

import salesDood2 from "../../src/utils/topline_logo.png";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Home,
  Users,
  Building2,
  Package,
  TrendingUp,
  Boxes,
  Bell,
  LogOut,
  GitBranch,
  ChevronUp,
} from "lucide-react";
import { ExecutiveMetricsGrid } from "./ExecutiveMetricsGrid";
import CompactCashFlow from "./CompactCashFlow";
import CompactReceivablesAging from "./CompactReceivablesAging";
import CompactProductPortfolio from "./CompactProductPortfolio";
import CompactActionFeed from "./CompactActionFeed";
import DailySales from "../popups/DailySales";
import useAutoScan from "./useAutoScan";
import FloatingScanButton from "./FloatingScanButton";
import QuickScan from "./QuickScan";
import NetProfit from "../popups/Netprofit";
import Receivables from "../popups/Receivables";
import CashBalance from "../popups/CashBalance";
import BankBalance from "../popups/BankBalance";

// Helper to map monthly text representations cleanly
const monthMap = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
  Jan: "Jan",
  Feb: "Feb",
  Mar: "Mar",
  Apr: "Apr",
  May: "May",
  Jun: "Jun",
  Jul: "Jul",
  Aug: "Aug",
  Sep: "Sep",
  Oct: "Oct",
  Nov: "Nov",
  Dec: "Dec",
};

// Sort chronologically and extract the dynamically passed values from apiGetFinancialDataRevenue.
const buildRevenueTrend = (apiData) => {
  if (!Array.isArray(apiData)) return [];
  return [...apiData]
    .sort((a, b) => a.SaleMonth.localeCompare(b.SaleMonth))
    .map((item) => {
      const monthKey = item.SaleMonth.split("-")[1];
      return {
        m: monthMap[monthKey] || monthKey,
        revenue: item.TotalSales,
        growth: item.GrowthPercent,
        avgSale: item.AvgSaleAmount,
        rawMonth: item.SaleMonth,
      };
    });
};

const dealsTrend = [
  { m: "Jan", deals: 38 },
  { m: "Feb", deals: 42 },
  { m: "Mar", deals: 45 },
  { m: "Apr", deals: 50 },
  { m: "May", deals: 52 },
  { m: "Jun", deals: 54 },
  { m: "Jul", deals: 56 },
];
const pipelineData = [
  { name: "Lead", value: 37, color: "#6366f1" },
  { name: "Qualified", value: 21, color: "#8b5cf6" },
  { name: "Demo", value: 10, color: "#a78bfa" },
  { name: "Negotiation", value: 8, color: "#c4b5fd" },
  { name: "Closed", value: 4, color: "#ddd6fe" },
];
const forecastData = [
  { q: "Q1", val: 2200 },
  { q: "Q2", val: 2600 },
  { q: "Q3", val: 2400 },
  { q: "Q4", val: 2800 },
];
const retentionTrend = [
  { m: "Jan", retention: 94, churn: 6 },
  { m: "Feb", retention: 95, churn: 5 },
  { m: "Mar", retention: 96, churn: 4 },
  { m: "Apr", retention: 95.5, churn: 4.5 },
  { m: "May", retention: 96.5, churn: 3.5 },
  { m: "Jun", retention: 97, strokeWidth: 2, churn: 3 },
  { m: "Jul", retention: 97, churn: 3 },
];
const npsPie = [
  { name: "Promoters", value: 66, color: "#10b981" },
  { name: "Passives", value: 25, color: "#f59e0b" },
  { name: "Detractors", value: 9, color: "#ef4444" },
];
const channelData = [
  { channel: "Organic Search", pct: 32.1 },
  { channel: "Paid Search", pct: 24.8 },
  { channel: "Social Media", pct: 18.4 },
  { channel: "Email", pct: 14.2 },
  { channel: "Referral", pct: 10.5 },
];
const marketingROI = [
  { campaign: "Product Launch", roi: 900 },
  { campaign: "Webinar Series", roi: 540 },
  { campaign: "Content Series", roi: 460 },
  { campaign: "Lead Gen", roi: 702 },
];
const leadsData = [
  { m: "Jan", leads: 850 },
  { m: "Feb", leads: 920 },
  { m: "Mar", leads: 1020 },
  { m: "Apr", leads: 1080 },
  { m: "May", leads: 1140 },
  { m: "Jun", leads: 1180 },
  { m: "Jul", text: 1200 },
];

// ─── INVENTORY DATA ──────────────────────────────────────────────────────────
const stockLevelTrend = [
  { m: "Jan", inStock: 8200, reserved: 1100 },
  { m: "Feb", inStock: 8650, reserved: 1180 },
  { m: "Mar", inStock: 7950, reserved: 1240 },
  { m: "Apr", inStock: 8400, reserved: 1290 },
  { m: "May", inStock: 9100, reserved: 1350 },
  { m: "Jun", inStock: 8800, reserved: 1410 },
  { m: "Jul", inStock: 9320, reserved: 1460 },
];
const stockoutTrend = [
  { m: "Jan", rate: 3.8 },
  { m: "Feb", rate: 3.2 },
  { m: "Mar", rate: 4.1 },
  { m: "Apr", rate: 2.9 },
  { m: "May", rate: 2.4 },
  { m: "Jun", rate: 2.1 },
  { m: "Jul", rate: 1.8 },
];
const valuationTrend = [
  { m: "Jan", value: 2.1 },
  { m: "Feb", value: 2.18 },
  { m: "Mar", value: 2.05 },
  { m: "Apr", value: 2.24 },
  { m: "May", value: 2.38 },
  { m: "Jun", value: 2.32 },
  { m: "Jul", value: 2.46 },
];
const reorderUrgency = [
  { name: "Critical", value: 12, color: "#ef4444" },
  { name: "Reorder Soon", value: 28, color: "#f59e0b" },
  { name: "Healthy", value: 60, color: "#10b981" },
];
const inventoryTurnsTrend = [
  { m: "Jan", turns: 5.8 },
  { m: "Feb", turns: 6.1 },
  { m: "Mar", turns: 5.6 },
  { m: "Apr", turns: 6.4 },
  { m: "May", turns: 6.9 },
  { m: "Jun", turns: 7.2 },
  { m: "Jul", turns: 7.5 },
];
const fillRateTrend = [
  { m: "Jan", rate: 94.2 },
  { m: "Feb", rate: 95.1 },
  { m: "Mar", rate: 93.8 },
  { m: "Apr", rate: 96.0 },
  { m: "May", rate: 96.8 },
  { m: "Jun", rate: 97.3 },
  { m: "Jul", rate: 97.9 },
];
const deadStockSplit = [
  { name: "Active", value: 82, color: "#10b981" },
  { name: "Slow-moving", value: 12, color: "#f59e0b" },
  { name: "Dead Stock", value: 6, color: "#ef4444" },
];
const shrinkageSplit = [
  { name: "Damage", value: 38, color: "#f59e0b" },
  { name: "Theft", value: 27, color: "#ef4444" },
  { name: "Admin Error", value: 35, color: "#6366f1" },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const CHART_COLOR_PRIMARY = "#6366f1";
const CHART_COLOR_SUCCESS = "#10b981";
const CHART_COLOR_WARNING = "#f59e0b";
const CHART_COLOR_DANGER = "#ef4444";

const TrendBadge = ({ value, suffix = "vs last month", up }) => {
  const isUp = up ?? !String(value).startsWith("-");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 12,
        fontWeight: 600,
        color: isUp ? CHART_COLOR_SUCCESS : CHART_COLOR_DANGER,
      }}
    >
      <span style={{ fontSize: 10 }}>{isUp ? "▲" : "▼"}</span>
      {value}{" "}
      <span style={{ fontWeight: 400, color: "#9ca3af" }}>{suffix}</span>
    </span>
  );
};

const SectionTitle = ({ icon, title }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}
  >
    <span style={{ fontSize: 16 }}>{icon}</span>
    <span style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>
      {title}
    </span>
    <div style={{ flex: 1 }} />
  </div>
);

const Card = ({ children, style = {} }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 14,
      padding: "12px 8px",
      width: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

const SubPanel = ({ children, style = {} }) => (
  <div
    style={{
      borderRadius: 10,
      border: "1.5px solid #f7f7f7",
      padding: "10px 6px",
      width: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

const MiniLabel = ({ children }) => (
  <div
    style={{
      fontSize: 9,
      color: "#94a3b8",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const BarProgress = ({ pct, color = CHART_COLOR_PRIMARY }) => (
  <div
    style={{
      height: 5,
      background: "#f1f5f9",
      borderRadius: 99,
      overflow: "hidden",
      marginTop: 4,
    }}
  >
    <div
      style={{
        width: `${pct}%`,
        height: "100%",
        background: color,
        borderRadius: 99,
      }}
    />
  </div>
);

// ─── TOP NAV ─────────────────────────────────────────────────────────────────
const TopNav = () => {
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);
  const [userName, setUserName] = useState("");

  const onSignOut = () => {
    //  clear other keys except company code , private key , public key so clear only particular keys
    localStorage.removeItem("currentUser");
    localStorage.removeItem("currentUserName");
    localStorage.removeItem("currentUserEmployeeID");
    localStorage.removeItem("currentSelectedDepartment");
    localStorage.removeItem("currentUserLoginStatus");

    navigate("/");
  };

  const handleClick = () => {
    navigate("/home_new");
  };

  useEffect(() => {
    const currentUserName = localStorage.getItem("currentUserName");
    let trimmedUsername = currentUserName?.trim();
    setUserName(trimmedUsername);

    const handleClickOutside = (e) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      style={{
        background: "#fff",
        borderBottom: "1px solid #e8edf5",
        padding: "10px 16px",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            onClick={handleClick}
            src={salesDood2}
            alt="Salesdood"
            className="rounded w-auto h-10 text-xl text-black font-semibold cursor-pointer "
          />
        </div>

        <div
          className="topnav-right"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <button
            aria-label="Notifications"
            style={{
              position: "relative",
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 9,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Bell size={16} color="#475569" strokeWidth={2} />
            <span
              style={{
                position: "absolute",
                top: 6,
                right: 7,
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: CHART_COLOR_DANGER,
                border: "1.5px solid #fff",
              }}
            />
          </button>

          <div style={{ position: "relative" }} ref={profileMenuRef}>
            <div
              onClick={() => setProfileMenuOpen((o) => !o)}
              role="button"
              tabIndex={0}
              aria-label="Account menu"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 9,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#6366f1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {userName ? userName[0]?.toUpperCase() : "-"}
              </div>
            </div>

            {profileMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 180,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                  padding: 6,
                  zIndex: 200,
                }}
              >
                <div
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #f1f5f9",
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}
                  >
                    {userName}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    onSignOut();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px 10px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#ef4444",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#fef2f2")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <LogOut size={15} color="#ef4444" strokeWidth={2} />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// ─── FINANCIAL PERFORMANCE ───────────────────────────────────────────────────
const FinancialSection = ({
  revenueTrend = [],
  salesForecastData = [],
  targetVsActualData = [],
}) => {
  const today = new Date();
  const currentYearMonth = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}`;

  const currentMonthObj =
    revenueTrend.find((item) => item.rawMonth === currentYearMonth) ||
    revenueTrend[revenueTrend.length - 1];

  const currentMonthRevenue = currentMonthObj ? currentMonthObj.revenue : 0;
  const rawGrowth = currentMonthObj ? currentMonthObj.growth : 0;
  const currentAvgSale = currentMonthObj ? currentMonthObj.avgSale : 0;

  const currentMonthGrowth = `${Number(rawGrowth).toFixed(2)}%`;
  const isUpTrend = rawGrowth >= 0;

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // ─── SALES FORECAST MAPPING ───
  const mappedSalesForecast = React.useMemo(() => {
    if (!Array.isArray(salesForecastData)) return [];
    return [...salesForecastData]
      .sort((a, b) => a.ForecastMonth.localeCompare(b.ForecastMonth))
      .map((item) => {
        const parts = item.ForecastMonth.split("-");
        const rawMonth = parts[1] || parts[0];
        return {
          m: monthMap[rawMonth] || rawMonth,
          "Forecasted Sales": item.ForecastedSales || 0,
        };
      });
  }, [salesForecastData]);

  const primaryForecastVal =
    Array.isArray(salesForecastData) && salesForecastData.length > 0
      ? salesForecastData[0].ForecastedSales
      : 0;

  // ─── TARGET VS ACTUAL MAPPING FIX ───
  const mappedTargetVsActual = React.useMemo(() => {
    const rawArray = targetVsActualData?.data || targetVsActualData;
    if (!Array.isArray(rawArray)) return [];

    return rawArray.map((item) => {
      // Extract the "05", "03", "02" numeric month substring from your "2026-05" format
      const monthKey = item.Month ? item.Month.split("-")[1] : "Period";

      return {
        // 1. Map to 'm' so <XAxis dataKey="m" /> reads it correctly (e.g., "May", "Mar")
        m: monthMap[monthKey] || monthKey,

        // 2. Map ForecastSales to 'Target' so <Bar dataKey="Target" /> reads it
        Target: item.ForecastSales || 0,

        // 3. Map ActualSales to 'Actual' so <Bar dataKey="Actual" /> reads it
        Actual: item.ActualSales || 0,
      };
    });
  }, [targetVsActualData]);

  // Dynamically parsing current month target metrics from endpoint state
  const currentTargetSalesObj = Array.isArray(targetVsActualData)
    ? targetVsActualData.find((item) => item.Month === currentYearMonth) ||
      targetVsActualData[0]
    : null;

  const latestActualSales = currentTargetSalesObj
    ? currentTargetSalesObj.ActualSales
    : 0;
  const latestForecastSales = currentTargetSalesObj
    ? currentTargetSalesObj.ForecastSales
    : 0;
  const dynamicAchievementPercent =
    latestForecastSales > 0
      ? `${((latestActualSales / latestForecastSales) * 100).toFixed(1)}%`
      : "No Target Set";

  return (
    <Card>
      <SectionTitle icon="📈" title="Financial Performance" />
      <div className="responsive-grid" style={{ display: "grid", gap: 20 }}>
        <SubPanel>
          <MiniLabel>Revenue</MiniLabel>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            {formatNumber(currentMonthRevenue)}
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: "#94a3b8",
                marginLeft: "10px",
              }}
            >
              this month
            </span>
          </div>
          <TrendBadge
            value={currentMonthGrowth}
            suffix="vs last period"
            up={isUpTrend}
          />
          <div style={{ marginTop: 12, height: 90 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={revenueTrend}
                margin={{ top: 4, right: 4, left: -10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="10%"
                      stopColor={CHART_COLOR_PRIMARY}
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="95%"
                      stopColor={CHART_COLOR_PRIMARY}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="m"
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v) => [`${v}`, "Revenue"]}
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={CHART_COLOR_PRIMARY}
                  strokeWidth={2}
                  fill="url(#revGrad)"
                  dot={true}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SubPanel>

        <SubPanel>
          <MiniLabel>Gross Margin & Profit</MiniLabel>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            {formatNumber(currentAvgSale)}
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: "#94a3b8",
                marginLeft: "6px",
              }}
            >
              Avg Sale
            </span>
          </div>
          <TrendBadge
            value={currentMonthGrowth}
            suffix="growth rate"
            up={isUpTrend}
          />
          <div style={{ marginTop: 12, height: 90 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={revenueTrend}
                margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="gpGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="10%"
                      stopColor={CHART_COLOR_SUCCESS}
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="95%"
                      stopColor={CHART_COLOR_SUCCESS}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="m"
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v) => [`${v}`, "Avg Sale Amount"]}
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="avgSale"
                  stroke={CHART_COLOR_SUCCESS}
                  strokeWidth={2}
                  fill="url(#gpGrad)"
                  dot={true}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SubPanel>

        {/* ─── RESTORED TARGET VS ACTUAL ─── */}
        <SubPanel>
          <MiniLabel>Target vs Actual Sales</MiniLabel>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            {formatNumber(latestActualSales)}
            <span
              style={{
                fontSize: 11,
                fontWeight: 400,
                color: "#94a3b8",
                marginLeft: "4px",
              }}
            >
              Actual Sales
            </span>
          </div>
          <TrendBadge
            value={dynamicAchievementPercent}
            suffix={latestForecastSales > 0 ? "achievement" : ""}
            up={latestActualSales >= latestForecastSales}
          />
          <div style={{ marginTop: 12, height: 90 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={mappedTargetVsActual}
                margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
              >
                <XAxis
                  dataKey="m"
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Bar
                  dataKey="Actual"
                  fill={CHART_COLOR_SUCCESS}
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="Target"
                  fill={CHART_COLOR_PRIMARY}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SubPanel>

        {/* ─── SALES FORECAST TREND ─── */}
        <SubPanel>
          <MiniLabel>Sales Forecast Trend</MiniLabel>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 2,
            }}
          >
            {formatNumber(primaryForecastVal)}
          </div>
          <div style={{ height: 75 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={mappedSalesForecast}
                margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
              >
                <XAxis
                  dataKey="m"
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip contentStyle={{ fontSize: 10 }} />
                <Bar
                  dataKey="Forecasted Sales"
                  fill={CHART_COLOR_PRIMARY}
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SubPanel>
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          paddingTop: 12,
          borderTop: "1px solid #f1f5f9",
        }}
      ></div>
    </Card>
  );
};

// ─── SALES & PIPELINE ─────────────────────────────────────────────────────────
const SalesSection = () => (
  <Card>
    <SectionTitle icon="🛒" title="Sales & Pipeline" />
    <div className="responsive-grid" style={{ display: "grid", gap: 20 }}>
      <SubPanel>
        <MiniLabel>Deals Closed (MTD)</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          56
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="7.7%" suffix="vs last period" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dealsTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Line
                type="monotone"
                dataKey="deals"
                stroke={CHART_COLOR_PRIMARY}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Pipeline Value</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          11.73M
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="3.0%" suffix="vs last period" up={true} />
        <div
          style={{
            marginTop: 12,
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <PieChart width={90} height={90}>
            <Pie
              data={pipelineData}
              cx={45}
              cy={45}
              innerRadius={28}
              outerRadius={42}
              dataKey="value"
              paddingAngle={2}
            >
              {pipelineData.map((e, i) => (
                <Cell key={i} fill={e.color} />
              ))}
            </Pie>
          </PieChart>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {pipelineData.map((e) => (
              <div
                key={e.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 9,
                  color: "#475569",
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: e.color,
                    flexShrink: 0,
                  }}
                />
                {e.name} {e.value}%
              </div>
            ))}
          </div>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Lead-To-Customer Rate</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          27%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="1.5%" suffix="vs last period" up={null} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dealsTrend.slice(0, 5)}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Bar dataKey="deals" fill="#a78bfa" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Q4 Forecast</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          2.8M
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="1.5%" suffix="vs last period" up={null} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={forecastData}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="q"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Bar
                dataKey="val"
                fill={CHART_COLOR_SUCCESS}
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>
    </div>
  </Card>
);

// ─── CUSTOMER SUCCESS ────────────────────────────────────────────────────────
const CustomerSection = () => (
  <Card>
    <SectionTitle icon="😊" title="Customer Success & Satisfaction" />
    <div className="responsive-grid" style={{ display: "grid", gap: 20 }}>
      <SubPanel>
        <MiniLabel>Customer Retention Rate</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          97.0%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="0.5%" suffix="vs last period" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={retentionTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <defs>
                <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="10%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="retention"
                stroke={CHART_COLOR_SUCCESS}
                strokeWidth={2}
                fill="url(#retGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Net Revenue Churn</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          3.0%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="-1.0%" suffix="vs last period" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={retentionTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="churn"
                stroke={CHART_COLOR_DANGER}
                strokeWidth={2}
                fill="none"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>NPS Score & Breakdown</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          +57
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /quarter
          </span>
        </div>
        <TrendBadge value="4.0" suffix="points growth" up={true} />
        <div
          style={{
            marginTop: 12,
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <PieChart width={90} height={90}>
            <Pie
              data={npsPie}
              cx={45}
              cy={45}
              innerRadius={28}
              outerRadius={42}
              dataKey="value"
              paddingAngle={2}
            >
              {npsPie.map((e, i) => (
                <Cell key={i} fill={e.color} />
              ))}
            </Pie>
          </PieChart>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {npsPie.map((e) => (
              <div
                key={e.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 9,
                  color: "#475569",
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: e.color,
                    flexShrink: 0,
                  }}
                />
                {e.name} {e.value}%
              </div>
            ))}
          </div>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Support SLA Achievement</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          94.2%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="1.8%" suffix="vs target" up={true} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginTop: 14,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
              }}
            >
              <span style={{ fontWeight: 600, color: "#334155" }}>
                First Response Time
              </span>
              <span style={{ color: CHART_COLOR_SUCCESS, fontWeight: 600 }}>
                98%
              </span>
            </div>
            <BarProgress pct={98} color={CHART_COLOR_SUCCESS} />
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
              }}
            >
              <span style={{ fontWeight: 600, color: "#334155" }}>
                Resolution Time
              </span>
              <span style={{ color: CHART_COLOR_WARNING, fontWeight: 600 }}>
                89%
              </span>
            </div>
            <BarProgress pct={89} color={CHART_COLOR_WARNING} />
          </div>
        </div>
      </SubPanel>
    </div>
  </Card>
);

// ─── MARKETING ACQUISITION ───────────────────────────────────────────────────
const MarketingSection = () => (
  <Card>
    <SectionTitle icon="📢" title="Marketing & Acquisition" />
    <div className="responsive-grid" style={{ display: "grid", gap: 20 }}>
      <SubPanel>
        <MiniLabel>Customer Acquisition Cost</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          1,240
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /blended
          </span>
        </div>
        <TrendBadge value="-4.2%" suffix="decreased cost" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dealsTrend.slice(2, 7)}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Bar dataKey="deals" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Traffic Mix by Channel</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          348K
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /uniques
          </span>
        </div>
        <TrendBadge value="11.2%" suffix="vs last period" up={true} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginTop: 10,
          }}
        >
          {channelData.slice(0, 4).map((c) => (
            <div key={c.channel}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 10,
                  color: "#475569",
                }}
              >
                <span>{c.channel}</span>
                <span style={{ fontWeight: 600 }}>{c.pct}%</span>
              </div>
              <BarProgress pct={c.pct} color={CHART_COLOR_PRIMARY} />
            </div>
          ))}
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Top Campaigns ROI</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          651%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /average
          </span>
        </div>
        <TrendBadge value="24%" suffix="performance surge" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={marketingROI}
              margin={{ top: 0, right: 10, left: -20, bottom: 0 }}
            >
              <XAxis type="number" hide />
              <YAxis
                dataKey="campaign"
                type="category"
                tick={{ fontSize: 8, fill: "#475569" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(v) => [`${v}%`, "ROI"]}
                contentStyle={{
                  fontSize: 10,
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Bar dataKey="roi" fill="#3b82f6" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Lead Gen Velocity</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          1,200
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            /month
          </span>
        </div>
        <TrendBadge value="1.7%" suffix="vs last period" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={leadsData}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="leads"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="none"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>
    </div>
  </Card>
);

// ─── INVENTORY SECTION ────────────────────────────────────────────────────────
const InventorySection = () => (
  <Card>
    <SectionTitle icon="📦" title="Inventory Management" />
    <div className="responsive-grid" style={{ display: "grid", gap: 20 }}>
      <SubPanel>
        <MiniLabel>Stock Levels Trend</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          9,320
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            units in stock
          </span>
        </div>
        <TrendBadge value="5.9%" suffix="vs last month" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={stockLevelTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <defs>
                <linearGradient id="stockGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={CHART_COLOR_PRIMARY}
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLOR_PRIMARY}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="inStock"
                stroke={CHART_COLOR_PRIMARY}
                strokeWidth={2}
                fill="url(#stockGrad)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="reserved"
                stroke={CHART_COLOR_WARNING}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="none"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Stockout Rate</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          1.8%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            disruption rate
          </span>
        </div>
        <TrendBadge value="-14.2%" suffix="improvement" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={stockoutTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Line
                type="monotone"
                dataKey="rate"
                stroke={CHART_COLOR_DANGER}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Inventory Valuation</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          2.46M
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            total asset value
          </span>
        </div>
        <TrendBadge value="6.0%" suffix="vs last month" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={valuationTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <defs>
                <linearGradient id="valGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0.2}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}M`}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={CHART_COLOR_SUCCESS}
                strokeWidth={2}
                fill="url(#valGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>SKU Status & Reorder Urgency</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          12 SKU
          <span style={{ fontSize: 12, fontWeight: 400, color: "#ef4444" }}>
            {" "}
            at critical risk
          </span>
        </div>
        <TrendBadge value="-2 SKU" suffix="resolved" up={true} />
        <div
          style={{
            marginTop: 12,
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <PieChart width={90} height={90}>
            <Pie
              data={reorderUrgency}
              cx={45}
              cy={45}
              innerRadius={28}
              outerRadius={42}
              dataKey="value"
              paddingAngle={2}
            >
              {reorderUrgency.map((e, i) => (
                <Cell key={i} fill={e.color} />
              ))}
            </Pie>
          </PieChart>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {reorderUrgency.map((e) => (
              <div
                key={e.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 9,
                  color: "#475569",
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: e.color,
                    flexShrink: 0,
                  }}
                />
                {e.name} ({e.value}%)
              </div>
            ))}
          </div>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Inventory Turnover Rate</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          7.5x
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            annualized turns
          </span>
        </div>
        <TrendBadge value="4.1%" suffix="efficiency gain" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={inventoryTurnsTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Bar
                dataKey="turns"
                fill={CHART_COLOR_PRIMARY}
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Order Fill Rate</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          97.9%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            service level
          </span>
        </div>
        <TrendBadge value="0.6%" suffix="vs last month" up={true} />
        <div style={{ marginTop: 12, height: 90 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={fillRateTrend}
              margin={{ top: 4, right: 4, left: -30, bottom: 0 }}
            >
              <defs>
                <linearGradient id="fillGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLOR_SUCCESS}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="m"
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: "#94a3b8" }}
                axisLine={false}
                tickLine={false}
                domain={[90, 100]}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Area
                type="monotone"
                dataKey="rate"
                stroke={CHART_COLOR_SUCCESS}
                strokeWidth={2}
                fill="url(#fillGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Dead Stock & Ageing</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          6.0%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            tied capital risk
          </span>
        </div>
        <TrendBadge value="Unchanged" suffix="from last period" up={true} />
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {deadStockSplit.map((item) => (
            <div key={item.name}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 10,
                  color: "#475569",
                }}
              >
                <span>{item.name}</span>
                <span style={{ fontWeight: 600 }}>{item.value}%</span>
              </div>
              <BarProgress pct={item.value} color={item.color} />
            </div>
          ))}
        </div>
      </SubPanel>

      <SubPanel>
        <MiniLabel>Shrinkage & Waste</MiniLabel>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0f172a",
            marginBottom: 4,
          }}
        >
          0.42%
          <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>
            {" "}
            of total inventory
          </span>
        </div>
        <TrendBadge value="-0.05%" suffix="reduction" up={true} />
        <div
          style={{
            marginTop: 12,
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <PieChart width={90} height={90}>
            <Pie
              data={shrinkageSplit}
              cx={45}
              cy={45}
              innerRadius={28}
              outerRadius={42}
              dataKey="value"
              paddingAngle={2}
            >
              {shrinkageSplit.map((e, i) => (
                <Cell key={i} fill={e.color} />
              ))}
            </Pie>
          </PieChart>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {shrinkageSplit.map((e) => (
              <div
                key={e.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 9,
                  color: "#475569",
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: e.color,
                    flexShrink: 0,
                  }}
                />
                {e.name} ({e.value}%)
              </div>
            ))}
          </div>
        </div>
      </SubPanel>
    </div>
  </Card>
);

// ─── BOTTOM NAVIGATION ───────────────────────────────────────────────────────
const BottomNav = ({ activeTab, setActiveTab }) => {
  const navItems = [
    { label: "Home", icon: Home },
    { label: "Sales", icon: TrendingUp },
    { label: "CRM", icon: Users },
    { label: "Customers", icon: Building2 },
    { label: "Inventory", icon: Package },
  ];

  return (
    <div
      className="mobile-bottom-nav"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        background: "#ffffff",
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)",
        paddingTop: "8px",
        zIndex: 1000,
        boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.05)",
      }}
    >
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.label;
        return (
          <button
            key={item.label}
            onClick={() => setActiveTab(item.label)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0,
              background: "none",
              border: "none",
              color: isActive ? "#6366f1" : "#94a3b8",
              cursor: "pointer",
              padding: "4px",
              flex: 1,
            }}
          >
            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
            <span style={{ fontSize: 10, fontWeight: isActive ? 600 : 500 }}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ─── MAIN APP COMPONENT ──────────────────────────────────────────────────────
export default function CEODashboard() {
  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  let baseUrl = apiPaths_from_local_storage[0].API_CONFIG;

  let company_code_from_local_storage = localStorage.getItem("companyCode")
    ? localStorage.getItem("companyCode")
    : "-";

  const {
    loading: loadingApiDataDailySalesKPI,
    data: apiDataDailySalesKPI,
    get: getApiDataDailySalesKPI,
  } = useApiFromGetPost();

  const {
    loading: loadingApiCollectionMTDKPI,
    data: apiCollectionMTDKPI,
    get: getApiCollectionMTDKPI,
  } = useApiFromGetPost();

  const {
    loading: loadingApiNetProfit,
    data: apiNetProfit,
    get: getApiNetProfit,
  } = useApiFromGetPost();

  const {
    loading: loadingApiPDCReceivedIssued,
    data: apiPDCReceivedIssued,
    get: getApiPDCReceivedIssued,
  } = useApiFromGetPost();

  const {
    loading: loadingGetApiReceivablesReport,
    data: apiGetApiReceivablesReport,
    get: getApiReceivablesReport,
  } = useApiFromGetPost();

  const {
    loading: loadingGetApiCashBankBalanceReport,
    data: apiGetApiCashBankBalanceReport,
    get: getApiCashBankBalanceReport,
  } = useApiFromGetPost();

  const {
    loading: loadingGetFinancialDataRevenue,
    data: apiGetFinancialDataRevenue,
    get: getApiFinancialDataRevenue,
  } = useApiFromGetPost();

  const {
    loading: loadingGetTargetVsActualSales,
    data: apiGetTargetVsActualSales,
    get: getTargetVsActualSales,
  } = useApiFromGetPost();

  const {
    loading: loadingGetSalesForecast,
    data: apiGetSalesForecast,
    get: getSalesForecast,
  } = useApiFromGetPost();

  const {
    loading: loadingBranchListOfThisUser,
    data: apiBranchListOfThisUser,
    get: getBranchListOfThisUser,
  } = useApiFromGetPost();

  const revenueTrend = React.useMemo(
    () => buildRevenueTrend(apiGetFinancialDataRevenue),
    [apiGetFinancialDataRevenue]
  );

  const loadingMap = {
    "Daily Sales": loadingApiDataDailySalesKPI,
    "Collection (MTD)": loadingApiCollectionMTDKPI,
    "Net Profit": loadingApiNetProfit,
    "PDC Received": loadingApiPDCReceivedIssued,
    "PDC Issued": loadingApiPDCReceivedIssued,
  };

  const dataMap = {
    "Daily Sales": apiDataDailySalesKPI,
    "Collection (MTD)": apiCollectionMTDKPI,
    "Net Profit": apiNetProfit,
    "PDC Received": apiPDCReceivedIssued,
    "PDC Issued": apiPDCReceivedIssued,
  };

  const today = new Date();
  let todayDate = today.toISOString().split("T")[0];

  let fromDate = todayDate;
  let toDate = todayDate;

  // const { scanning, triggerScan } = useAutoScan({
  //   apiMap: {
  //     "Daily Sales": () =>
  //       getApiDataDailySalesKPI(
  //         `https://cubixweberp.com:213/api/HRDashboard/${company_code_from_local_storage}/MAIN%20%20%20%20%20%20/Todays_Sales_Branch/${fromDate}/${toDate}/-/-/-/-/-`
  //       ),
  //     "Collection (MTD)": () =>
  //       getApiCollectionMTDKPI(
  //         `https://cubixweberp.com:213/api/HRDashboard/${company_code_from_local_storage}/MAIN%20%20%20%20%20%20/Todays_Sales_Branch/${fromDate}/${toDate}/-/-/-/-/-`
  //       ),
  //   },
  //   loadingMap: {
  //     "Daily Sales": loadingApiDataDailySalesKPI,
  //     "Collection (MTD)": loadingApiCollectionMTDKPI,
  //   },
  // });

  const [showKPIPopup, setShowKPIPopup] = useState("");
  const [activeTab, setActiveTab] = useState("Home");
  const [isMobile, setIsMobile] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState("All");
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);
  const branchPillRef = useRef(null);

  // MOBILE DETECTOR
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // CLOSE BRANCH DROPDOWN ON CLICK OUTSIDE
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsBranchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const callApis = () => {
    const today = new Date();
    let todayDate = today.toISOString().split("T")[0];

    let fromDate = todayDate;
    let toDate = todayDate;

    const branchParam = selectedBranch === "All" ? "-" : selectedBranch;

    let urlDailySalesKPI = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Todays_Sales_Branch/${fromDate}/${toDate}/-/-/-/-/-`;

    let urlApiNetProfit = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Profit_Analysis_Company/${fromDate}/${toDate}/-/-/-/-/-`;
    let urlApiPDCReceivedIssued = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/CASH_FUND_FORCAST_30DAYS/2024-01-01/${toDate}/-/-/-/-/-`;
    let urlApiReceivables = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/AGEINGREPORT/2024-01-01/${toDate}/-/-/-/-/-`;
    let urlApiCashBankBalanceReport = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/Cash_And_Bank_Position/2024-01-01/${toDate}/-/-/-/-/-`;
    let urlFinancialDataRevenue = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/MONTH_SALES_COMP/2024-01-01/${toDate}/-/-/-/-/-`;
    let urlTargetVsActual = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/SALES_TARGET_ANALYSIS/2024-01-01/${toDate}/-/-/-/-/-`;
    let urlSalesForeCast = `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/${branchParam}/SALES_FORECAST/2024-01-01/${toDate}/-/-/-/-/-`;

    getApiDataDailySalesKPI(urlDailySalesKPI);
    getApiCollectionMTDKPI("");
    getApiNetProfit(urlApiNetProfit);
    getApiPDCReceivedIssued(urlApiPDCReceivedIssued);
    getApiReceivablesReport(urlApiReceivables);
    getApiCashBankBalanceReport(urlApiCashBankBalanceReport);
    getApiFinancialDataRevenue(urlFinancialDataRevenue);
    getTargetVsActualSales(urlTargetVsActual);
    getSalesForecast(urlSalesForeCast);
  };

  const branchOptionsWithAll = React.useMemo(() => {
    if (!apiBranchListOfThisUser) return [];
    return [{ DEPTNO: "All", deptnodesc: "All" }, ...apiBranchListOfThisUser];
  }, [apiBranchListOfThisUser]);

  useEffect(() => {
    if (apiBranchListOfThisUser) {
      if (apiBranchListOfThisUser?.length > 0) {
        callApis();
      }
    }
  }, [apiBranchListOfThisUser, selectedBranch]);

  useEffect(() => {
    const currentUserName = localStorage.getItem("currentUserName");
    let trimmedUsername = currentUserName?.trim();
    let urlBranchOfUser = `${baseUrl}/api/MasterCount/${company_code_from_local_storage}/BRANCH/${trimmedUsername}/-`;
    console.log("urlBranchOfUser ", urlBranchOfUser);
    getBranchListOfThisUser(urlBranchOfUser);
  }, []);

  const openKPIPopup = (kpicardname) => {
    setShowKPIPopup(kpicardname);
  };

  return (
    <div
      style={{
        background: "#eef2f7",
        minHeight: "100vh",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Inter', system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <style>{`
        *, *:before, *:after {
          box-sizing: border-box !important;
        }
        .responsive-grid {
          grid-template-columns: repeat(4, 1fr);
        }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        
        @media (max-width: 1024px) {
          .responsive-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .responsive-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important; 
            gap: 10px !important; 
            width: 100% !important;
          }
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px;
            width:94vw;
          }
          .desktop-nav-links, .desktop-nav-right {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .mobile-bottom-nav {
            display: flex !important;
          }
          .page-wrapper {
            padding: 2px 2px 100px !important;
          }
        }
      `}</style>
      <div className="page-wrapper" style={{ margin: "0 auto" }}>
        <TopNav />

        <div
          style={{ display: "flex", flexDirection: "column", padding: "8px" }}
        >
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <ExecutiveMetricsGrid
              openKPIPopup={openKPIPopup}
              closeKPIPopup={() => setShowKPIPopup(null)}
              loadingMap={loadingMap}
              dataMap={dataMap}
            />
          </div>

          <div style={{width: "100%", maxWidth: 1280, margin: "0 auto" }}>
            {activeTab === "Home" && (
              <FinancialSection
                revenueTrend={revenueTrend}
                salesForecastData={apiGetSalesForecast}
                targetVsActualData={apiGetTargetVsActualSales}
              />
            )}
            {/* {activeTab === "Sales" && <SalesSection />}
          {activeTab === "CRM" && <MarketingSection />}
          {activeTab === "Customers" && <CustomerSection />}
          {activeTab === "Inventory" && <InventorySection />} */}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "12px",
              padding: "12px",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
      {/* Direct Ref Floating Pill Wrapper */}
      
        <div
          ref={branchPillRef}
          style={{
            position: "fixed",
            bottom: 76,
            right: "10px",
            zIndex: 999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100px",
          }}
        >
          <div ref={dropdownRef} style={{ width: "100%" }}>
            {isBranchDropdownOpen && (
              <div
                style={{
                  width: "100%",
                  background: "#ffffff",
                  borderRadius: "14px",
                  padding: "6px",
                  boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.15)",
                  border: "1px solid #e2e8f0",
                  marginBottom: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  maxHeight: "240px",
                  overflowY: "auto",
                }}
              >
                {branchOptionsWithAll?.map((branch) => {
                  const isSelected = selectedBranch === branch.DEPTNO;
                  return (
                    <button
                      key={branch.DEPTNO}
                      onClick={() => {
                        setSelectedBranch(branch.DEPTNO);
                        setIsBranchDropdownOpen(false);
                      }}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "none",
                        background: isSelected
                          ? `${CHART_COLOR_PRIMARY}12`
                          : "transparent",
                        color: isSelected ? CHART_COLOR_PRIMARY : "#334155",
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "12px",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      {branch.DEPTNO}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setIsBranchDropdownOpen(!isBranchDropdownOpen)}
              style={{
                width: "100%",
                background: CHART_COLOR_PRIMARY,
                color: "#ffffff",
                border: "none",
                borderRadius: "999px",
                padding: "10px 18px",
                fontSize: "12px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
                cursor: "pointer",
              }}
            >
              <GitBranch size={14} />
              <span
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedBranch}
              </span>
              <ChevronUp
                size={14}
                style={{
                  transform: isBranchDropdownOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  marginLeft: "auto",
                }}
              />
            </button>
          </div>
        </div>
      
      {/* <FloatingScanButton
        rows={[
          {
            key: "Daily Sales",
            label: "Daily Sales",
            result: apiDataDailySalesKPI,
            loading: loadingApiDataDailySalesKPI,
          },
          {
            key: "Collection (MTD)",
            label: "Collection (MTD)",
            result: apiCollectionMTDKPI,
            loading: loadingApiCollectionMTDKPI,
          },
        ]}
        onSelectionChange={(keys) => console.log("saved:", keys)}
        scanning={scanning}
        onClick={triggerScan}
        bottom={70}
        left={20}
      /> */}
      {showKPIPopup === "Daily Sales" && (
        <Modal
          isOpen={true}
          onClose={() => setShowKPIPopup(null)}
          title="Daily Sales"
        >
          <DailySales data={apiDataDailySalesKPI} />
        </Modal>
      )}
      {showKPIPopup === "Net Profit" && (
        <Modal
          isOpen={true}
          onClose={() => setShowKPIPopup(null)}
          title="Net Profit"
        >
          <NetProfit data={apiNetProfit} />
        </Modal>
      )}
      {showKPIPopup === "Receivables" && (
        <Modal
          isOpen={true}
          onClose={() => setShowKPIPopup(null)}
          title="Receivables"
        >
          <Receivables data={apiGetApiReceivablesReport} />
        </Modal>
      )}
      {showKPIPopup === "Cash Balance" && (
        <Modal
          isOpen={true}
          onClose={() => setShowKPIPopup(null)}
          title="Cash Balance"
        >
          <CashBalance data={apiGetApiCashBankBalanceReport} />
        </Modal>
      )}

      {showKPIPopup === "Bank Balance" && (
        <Modal
          isOpen={true}
          onClose={() => setShowKPIPopup(null)}
          title="Bank Balance"
        >
          <BankBalance data={apiGetApiCashBankBalanceReport} />
        </Modal>
      )}

      {/* Direct Ref Bottom Navigation Element */}
      
        <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      
    </div>
  );
}
